import { ListingCardData } from "@/types/types";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

type ListingMeta = {
  is_saved: boolean;
  version: number;
};

type CacheState = {
  listings: Record<string, ListingCardData>;
  meta: Record<string, ListingMeta>;
  setListing: (listing: ListingCardData) => void;
  setMultipleListings: (listings: ListingCardData[]) => void;
  updateSavedState: (
    id: string,
    saved: boolean,
    fromBroadcast?: boolean,
  ) => void;
};

// 1. Initialize the Broadcast Channel safely (only on the client)
const syncChannel =
  typeof window !== "undefined"
    ? new BroadcastChannel("listings-saved-sync")
    : null;

export const useListingsCacheStore = create<CacheState>()(
  subscribeWithSelector((set) => ({
    listings: {},
    meta: {},

    setListing: (listing) =>
      set((state) => {
        const id = listing.listing_id;
        return {
          listings: {
            ...state.listings,
            [id]: listing,
          },
          meta: {
            ...state.meta,
            [id]: {
              is_saved: listing.is_saved ?? state.meta[id]?.is_saved ?? false,
              version: (state.meta[id]?.version ?? 0) + 1,
            },
          },
        };
      }),

    setMultipleListings: (listings) =>
      set((state) => {
        if (!Array.isArray(listings) || listings.length === 0) return state;

        const newListings: Record<string, ListingCardData> = {};
        const newMeta: Record<string, ListingMeta> = {};

        for (const listing of listings) {
          const id = listing.listing_id;
          newListings[id] = listing;
          newMeta[id] = {
            is_saved: listing.is_saved ?? state.meta[id]?.is_saved ?? false,
            version: (state.meta[id]?.version ?? 0) + 1,
          };
        }

        return {
          listings: { ...state.listings, ...newListings },
          meta: { ...state.meta, ...newMeta },
        };
      }),

    updateSavedState: (id, saved, fromBroadcast = false) => {
      set((state) => {
        // Only update if the listing exists in this tab's memory
        if (!state.meta[id]) return state;

        return {
          meta: {
            ...state.meta,
            [id]: {
              ...state.meta[id],
              is_saved: saved,
              version: (state.meta[id]?.version ?? 0) + 1,
            },
          },
        };
      });

      // 2. Broadcast to other tabs! (Ignore if the update CAME from a broadcast)
      if (!fromBroadcast && syncChannel) {
        syncChannel.postMessage({ id, saved });
      }
    },
  })),
);

// 3. Listen for other tabs saving/unsaving items
if (syncChannel) {
  syncChannel.onmessage = (event) => {
    const { id, saved } = event.data;
    // Apply the update, passing `true` so it doesn't broadcast it right back
    useListingsCacheStore.getState().updateSavedState(id, saved, true);
  };
}
